import { Flex, styled } from "styled-system/jsx";
import { AssetFile, Policy } from "gql/generated";
import { Button, Heading, Link, Text } from "@ttc3k/trekker";
import { ConsumingOverlayPageProps, OverlayPage } from "../OverlayPage";
import { getCloudinary } from "shared/cloudinary";
import { useTranslation } from "react-i18next";
import { useBookerContext } from "widgets/booker/contexts";
import { getMultilingualString } from "shared/utils";

type PoliciesPageProps = ConsumingOverlayPageProps & {
  policies?: Policy[];
  waivers?: AssetFile[];
  acceptedPolicies: boolean;
  acceptPolicies: () => void;
};

export const PoliciesPage = ({
  policies,
  waivers,
  acceptedPolicies,
  onClose,
  acceptPolicies,
  ...rest
}: PoliciesPageProps) => {
  const handleButtonClick = () => {
    acceptPolicies();
    onClose();
  };

  const { language } = useBookerContext();
  const { t } = useTranslation("booker", { keyPrefix: "POLICIES" });
  return (
    <OverlayPage
      title={t("TITLE")}
      onButtonClick={handleButtonClick}
      onClose={onClose}
      buttonDisabled={acceptedPolicies}
      buttonLabel={acceptedPolicies ? t("ACCEPTED") : t("ACCEPT")}
      {...rest}
    >
      <Flex flexDir={"column"} gap={"400"} my={"400"}>
        {waivers && waivers.length > 0 && (
          <Flex flexDir={"column"} gap={"200"}>
            <Heading size={"h5"}>{t("WAIVERS.TITLE")}</Heading>
            <Text color={"text.mid"}>{t("WAIVERS.SUBTITLE")}</Text>
            {waivers.map((waiver, i) => {
              const waiverUrl = getCloudinary(waiver._id).image(waiver._id).toURL();
              return (
                <styled.a
                  href={waiverUrl}
                  target="_blank"
                  width={"full"}
                  key={waiver._id}
                >
                  <Button visual={"accent.blue"} width={"full"} type="button">
                    {waiver.caption
                      ? getMultilingualString(waiver.caption, language)
                      : `Waiver ${i + 1}`}
                  </Button>
                </styled.a>
              );
            })}
          </Flex>
        )}
        {policies &&
          policies.map(policy => (
            <Flex flexDir={"column"} gap={"200"} key={policy._id}>
              <Heading size={"h5"}>
                {getMultilingualString(policy.name, language)}
              </Heading>
              <Text color={"text.mid"} whiteSpace={"pre-wrap"}>
                {getMultilingualString(policy.content, language)}
              </Text>
            </Flex>
          ))}
        <Text color={"text.dark"}>
          {t("DEFAULT_TERMS")}&nbsp;
          <Link
            href={
              "https://www.whereabouts.tech/terms-of-service/additional-terms-of-service-for-business-customers"
            }
            target="_blank"
          >
            {t("WB_TERMS")}
          </Link>
        </Text>
      </Flex>
    </OverlayPage>
  );
};
